import { useEffect } from "react";

export function useParallax(routeKey: string) {
  useEffect(() => {
    if (routeKey === "/" || routeKey.startsWith("/lore") || routeKey === "/discover") return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let frame = 0;
    let storyFrame = 0;
    let pointerX = 0;
    let pointerY = 0;
    const story = document.querySelector<HTMLElement>(".continuous-story");
    const storyBackdrop = document.querySelector<HTMLElement>(".continuous-story__backdrop");
    const storyImage = document.querySelector<HTMLImageElement>(".continuous-story__backdrop img");
    let storyTop = 0;
    let storyDistance = 1;
    let storyMaxPan = 0;
    let storyTargetPan = 0;
    let storyRenderedPan = 0;
    const userAgent = window.navigator.userAgent;
    const isSafari = window.navigator.vendor.includes("Apple")
      && /Safari/.test(userAgent)
      && !/(CriOS|FxiOS|EdgiOS|OPiOS)/.test(userAgent);
    const safariMajorVersion = Number(userAgent.match(/Version\/(\d+)/)?.[1] ?? 0);
    const useNativeStoryScroll = isSafari
      && /Macintosh/.test(userAgent)
      && safariMajorVersion > 0
      && safariMajorVersion < 26;

    story?.classList.toggle("continuous-story--native-scroll", useNativeStoryScroll);
    if (useNativeStoryScroll) storyImage?.style.removeProperty("transform");

    const renderStoryPosition = (pan: number) => {
      if (!storyImage || useNativeStoryScroll) return;
      const deviceScale = Math.max(1, window.devicePixelRatio || 1);
      const snappedPan = isSafari ? Math.round(pan * deviceScale) / deviceScale : pan;
      storyImage.style.transform = `translate3d(-50%, ${snappedPan}px, 0)`;
    };

    const updateStoryPosition = () => {
      storyFrame = 0;
      if (!story || !storyImage || !storyBackdrop || useNativeStoryScroll) return;
      const progress = Math.min(1, Math.max(0, (window.scrollY - storyTop) / storyDistance));
      storyTargetPan = -progress * storyMaxPan;

      if (isSafari) {
        // Safari 18 repaints a very tall, sticky image unevenly when its transform
        // is retargeted directly from scroll events. Ease across those event gaps
        // and snap only the final raster position to the physical-pixel grid.
        const difference = storyTargetPan - storyRenderedPan;
        storyRenderedPan = Math.abs(difference) < 0.1
          ? storyTargetPan
          : storyRenderedPan + difference * 0.24;
        renderStoryPosition(storyRenderedPan);
        if (storyRenderedPan !== storyTargetPan) scheduleStory();
        return;
      }

      storyRenderedPan = storyTargetPan;
      renderStoryPosition(storyRenderedPan);
    };

    const scheduleStory = () => {
      if (!storyFrame) storyFrame = window.requestAnimationFrame(updateStoryPosition);
    };

    const refreshStoryMetrics = () => {
      if (!story || !storyImage || !storyBackdrop || useNativeStoryScroll) return;
      const viewportHeight = storyBackdrop.clientHeight;
      storyTop = story.offsetTop;
      storyDistance = Math.max(1, story.offsetHeight - viewportHeight);
      storyMaxPan = Math.max(0, storyImage.offsetHeight - viewportHeight);
      const progress = Math.min(1, Math.max(0, (window.scrollY - storyTop) / storyDistance));
      storyTargetPan = -progress * storyMaxPan;
      if (!isSafari || storyRenderedPan === 0) storyRenderedPan = storyTargetPan;
      scheduleStory();
    };

    const update = () => {
      frame = 0;
      if (!story) document.documentElement.style.setProperty("--page-scroll", `${window.scrollY}px`);
      document.documentElement.style.setProperty("--pointer-x", reduceMotion ? "0" : pointerX.toFixed(4));
      document.documentElement.style.setProperty("--pointer-y", reduceMotion ? "0" : pointerY.toFixed(4));
      document.documentElement.style.setProperty("--pointer-x-px", `${(pointerX * 22).toFixed(2)}px`);
      document.documentElement.style.setProperty("--pointer-y-px", `${(pointerY * 22).toFixed(2)}px`);
      document.documentElement.style.setProperty("--scroll-bg", `${(window.scrollY * -0.012).toFixed(2)}px`);
      document.documentElement.style.setProperty("--scroll-bg-deep", `${(window.scrollY * -0.018).toFixed(2)}px`);
      document.documentElement.style.setProperty("--scroll-bg-soft", `${(window.scrollY * -0.009).toFixed(2)}px`);
      document.documentElement.style.setProperty("--scroll-copy", `${(window.scrollY * 0.018).toFixed(2)}px`);
      document.documentElement.style.setProperty("--scroll-ribbon-x", `${(window.scrollY * -0.075).toFixed(2)}px`);
      document.documentElement.style.setProperty("--scroll-ribbon-y", `${(window.scrollY * -0.035).toFixed(2)}px`);
      document.documentElement.style.setProperty("--scroll-rail", `${(window.scrollY * -0.018).toFixed(2)}px`);
      document.documentElement.style.setProperty("--pointer-copy-x", `${(pointerX * -4.8).toFixed(2)}px`);
      document.documentElement.style.setProperty("--pointer-near-x", `${(pointerX * 24).toFixed(2)}px`);
      document.documentElement.style.setProperty("--pointer-near-y", `${(pointerY * 14).toFixed(2)}px`);
      document.documentElement.style.setProperty("--pointer-mid-x", `${(pointerX * 15).toFixed(2)}px`);
      document.documentElement.style.setProperty("--pointer-mid-y", `${(pointerY * 9).toFixed(2)}px`);
      document.documentElement.style.setProperty("--pointer-far-x", `${(pointerX * -6).toFixed(2)}px`);
      document.documentElement.style.setProperty("--pointer-far-y", `${(pointerY * -6).toFixed(2)}px`);
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    const onScroll = () => {
      if (story && !useNativeStoryScroll) scheduleStory();
      else if (!story) schedule();
    };
    const onPointerMove = (event: PointerEvent) => {
      pointerX = (event.clientX / window.innerWidth - 0.5) * 2;
      pointerY = (event.clientY / window.innerHeight - 0.5) * 2;
      schedule();
    };
    update();
    if (!useNativeStoryScroll) refreshStoryMetrics();
    window.addEventListener("scroll", onScroll, { passive: true });
    if (!useNativeStoryScroll) window.addEventListener("resize", refreshStoryMetrics, { passive: true });
    if (!useNativeStoryScroll) storyImage?.addEventListener("load", refreshStoryMetrics);
    const storyResizeObserver = !useNativeStoryScroll && story && storyImage && storyBackdrop
      ? new ResizeObserver(refreshStoryMetrics)
      : undefined;
    if (storyResizeObserver && story && storyImage && storyBackdrop) {
      storyResizeObserver.observe(story);
      storyResizeObserver.observe(storyBackdrop);
      storyResizeObserver.observe(storyImage);
    }
    if (!reduceMotion && !story) window.addEventListener("pointermove", onPointerMove, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (!useNativeStoryScroll) window.removeEventListener("resize", refreshStoryMetrics);
      if (!useNativeStoryScroll) storyImage?.removeEventListener("load", refreshStoryMetrics);
      storyResizeObserver?.disconnect();
      if (!reduceMotion && !story) window.removeEventListener("pointermove", onPointerMove);
      if (frame) cancelAnimationFrame(frame);
      if (storyFrame) cancelAnimationFrame(storyFrame);
      story?.classList.remove("continuous-story--native-scroll");
      storyImage?.style.removeProperty("transform");
    };
  }, [routeKey]);
}
